import { supabase } from '../lib/supabase'

export type HelpdeskLogAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  | 'export'
  | 'import'
  | 'login'
  | 'logout'
  | 'other'

export interface HelpdeskLogMetadata {
  [key: string]: unknown
  old_values?: Record<string, unknown>
  new_values?: Record<string, unknown>
  filters?: Record<string, unknown>
  additional_info?: string
}

export interface HelpdeskUserActivityLog {
  id: string
  user_id: string
  action: string
  entity_type: string
  entity_id?: string | null
  description: string
  metadata?: HelpdeskLogMetadata | null
  page_url?: string | null
  ip_address?: string | null
  user_agent?: string | null
  created_at: string
}

export type HelpdeskLogWithUser = HelpdeskUserActivityLog & {
  profiles?: { id: string; name: string | null } | null
}

export interface HelpdeskGetLogsFilters {
  userId?: string
  action?: string
  entityType?: string
  entityId?: string
  dateFrom?: string
  dateTo?: string
  search?: string
  page?: number
  limit?: number
}

class HelpdeskUserLogsService {
  async listDistinctEntityTypes(filters?: { userId?: string }): Promise<string[]> {
    try {
      let query = supabase
        .from('helpdesk_user_activity_logs')
        .select('entity_type')
        .order('entity_type', { ascending: true })
        .limit(5000)

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId)
      }

      const { data, error } = await query

      if (error) {
        throw error
      }

      const unique = Array.from(
        new Set(
          (data || [])
            .map((row) => String(row.entity_type || '').trim())
            .filter(Boolean)
        )
      )

      return unique.sort((a, b) => a.localeCompare(b, 'pt-BR'))
    } catch (error) {
      console.error('Erro ao listar tipos dos logs do helpdesk:', error)
      throw error
    }
  }

  async getLogs(
    filters?: HelpdeskGetLogsFilters
  ): Promise<{ logs: HelpdeskLogWithUser[]; total: number }> {
    try {
      let query = supabase
        .from('helpdesk_user_activity_logs')
        .select(
          `
          *,
          profiles!helpdesk_user_activity_logs_user_id_fkey(id, name)
        `,
          { count: 'exact' }
        )
        .order('created_at', { ascending: false })

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId)
      }

      if (filters?.action) {
        query = query.eq('action', filters.action)
      }

      if (filters?.entityType) {
        query = query.eq('entity_type', filters.entityType)
      }

      if (filters?.entityId) {
        query = query.eq('entity_id', filters.entityId)
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom)
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo)
      }

      if (filters?.search) {
        query = query.ilike('description', `%${filters.search}%`)
      }

      const page = filters?.page || 1
      const limit = filters?.limit || 20
      const from = (page - 1) * limit
      const to = from + limit - 1

      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) {
        throw error
      }

      return {
        logs: (data || []) as HelpdeskLogWithUser[],
        total: count || 0,
      }
    } catch (error) {
      console.error('Erro ao buscar logs do helpdesk:', error)
      throw error
    }
  }
}

export const helpdeskUserLogsService = new HelpdeskUserLogsService()
